import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Dialog,
  DialogContent
} from '@mui/material';
import axios from 'axios';
import { isMobile, isTablet } from 'react-device-detect'; // ✅ import

interface AgentFormData {
  name: string;
  mobile: string;
  csp_code: string;
}

interface QrResponse {
  id: number;
  csp_code: string;
  qr_payload: string;
  qr_image_data_url: string;
  message: string;
}

export default function AgentForm() {
  const [formData, setFormData] = useState<AgentFormData>({
    name: '',
    mobile: '',
    csp_code: ''
  });
  const [qrData, setQrData] = useState<QrResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 🚫 Block mobile/tablet
    if (isMobile || isTablet) {
      setError("⚠️ This form can only be submitted on a desktop/laptop.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Geolocation is not supported by this browser."));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude: lat, longitude: lng, accuracy } = position.coords;

      const response = await axios.post<QrResponse>('https://csp-tracking-api.drishtee.in/api/agents', {
        ...formData,
        lat,
        lng,
        accuracy
      });

      if (response.status === 201 || response.status === 200) {
        setQrData(response.data);
      } else {
        setError(response.data?.message || "Unexpected server response");
      }
    } catch (err: any) {
      console.error("❌ handleSubmit error:", err);

      if (err?.code && typeof err.code === "number") {
        switch (err.code) {
          case 1:
            setError("Permission denied. Please allow location access.");
            break;
          case 2:
            setError("Position unavailable. Please check your GPS.");
            break;
          case 3:
            setError("Location request timed out. Try again.");
            break;
          default:
            setError("Geolocation error occurred.");
        }
        return;
      }

      if (axios.isAxiosError(err)) {
        if (err.response) {
          const msg =
            typeof err.response.data === "string"
              ? err.response.data
              : err.response.data?.message || `Server error: ${err.response.status}`;
          setError(msg);
        } else if (err.request) {
          setError("No response from server. Please check your connection.");
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : "An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const isDesktop = !(isMobile || isTablet);

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <img
          src="/drishtee-logo.png"
          alt="Drishtee Logo"
          style={{ maxWidth: '100px', marginBottom: '16px' }}
        />
        <Typography variant="h6" gutterBottom>
          Hi CSP, please confirm your information
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        {!isDesktop && (
          <Typography color="error" sx={{ mb: 2 }}>
            ⚠️ This form must be filled on a desktop/laptop (not mobile/tablet).
          </Typography>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            margin="normal"
          />
          <TextField
            fullWidth
            label="Mobile"
            name="mobile"
            value={formData.mobile}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/\D/g, "");
              if (onlyNums.length <= 10) {
                setFormData((prev) => ({
                  ...prev,
                  mobile: onlyNums,
                }));
              }
            }}
            required
            margin="normal"
            placeholder="9876543210"
            inputProps={{ maxLength: 10 }}
            error={formData.mobile.length > 0 && formData.mobile.length !== 10}
            helperText={
              formData.mobile.length > 0 && formData.mobile.length !== 10
                ? "Mobile number must be exactly 10 digits"
                : ""
            }
          />

          <TextField
            fullWidth
            label="CSP Code"
            name="csp_code"
            value={formData.csp_code}
            onChange={handleChange}
            required
            margin="normal"
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading || !isDesktop}
          >
            {loading ? 'Submitting...' : 'Generate QR Code'}
          </Button>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </form>
      </Paper>

      <Dialog open={Boolean(qrData)} onClose={() => setQrData(null)}>
        <DialogContent>
          {qrData && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Ask your executive to scan the QR
              </Typography>
              <img
                src={qrData.qr_image_data_url}
                alt="QR Code"
                style={{ maxWidth: '100%' }}
              />
              <Typography variant="body1" sx={{ mt: 2 }}>
                CSP Code: {qrData.csp_code}
              </Typography>
              <Typography variant="body2" sx={{ mt: 3 }}>
                Please don't close the screen until scan is complete.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Contact achintya@drishtee.in immediately for any emergency
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
